import axios from 'axios';

import { API } from '../constants/api';

import { axiosInterceptor } from './interceptors';

/**
 * Fetch order chat messages with pagination support via lastId cursor
 */
export const getOrderChatMessages = async (orderId: string | number, params?: { lastId?: string | number | null; limit?: number }) => {
    const response = await axiosInterceptor.get(`${API.CHAT.GET_MESSAGES}/messages`, {
        params: {
            entityType: 'order',
            entityId: orderId,
            chatType: 'customer',
            limit: 25,
            ...params,
        },
    });

    return response;
};

/**
 * Mark order chat messages as read
 */
export const markOrderChatAsRead = async (orderId: string | number) => {
    const response = await axiosInterceptor.post(`${API.CHAT.GET_MESSAGES}/mark-as-read`, {
        entityType: 'order',
        entityId: orderId,
        chatType: 'customer',
    });

    return response;
};

/**
 * Fetch order chat members (connected users)
 */
export const getOrderChatMembers = async (orderId: string | number) => {
    const response = await axiosInterceptor.get(`${API.CHAT.GET_MESSAGES}/members`, {
        params: {
            entityType: 'order',
            entityId: orderId,
            chatType: 'customer',
        },
    });

    return response;
};

/**
 * Fetch quote chat messages with pagination support via lastId cursor
 */
export const getQuoteChatMessages = async (quoteId: string | number, params?: { lastId?: string | number | null; limit?: number }) => {
    const response = await axiosInterceptor.get(`${API.CHAT.GET_MESSAGES}/messages`, {
        params: {
            entityType: 'quote',
            entityId: quoteId,
            chatType: 'customer',
            limit: 25,
            ...params,
        },
    });

    return response;
};

/**
 * Mark quote chat messages as read
 */
export const markQuoteChatAsRead = async (quoteId: string | number) => {
    const response = await axiosInterceptor.post(`${API.CHAT.GET_MESSAGES}/mark-as-read`, {
        entityType: 'quote',
        entityId: quoteId,
        chatType: 'customer',
    });

    return response;
};

/**
 * Fetch quote chat members (connected users)
 */
export const getQuoteChatMembers = async (quoteId: string | number) => {
    const response = await axiosInterceptor.get(`${API.CHAT.GET_MESSAGES}/members`, {
        params: {
            entityType: 'quote',
            entityId: quoteId,
            chatType: 'customer',
        },
    });

    return response;
};

/**
 * Send customer chat message via REST API
 * @param payload - Message payload containing quoteId, content, etc.
 * @param files - Array of File objects to attach
 * @param authorizationToken - Authorization token to pass as separate field
 * @returns Promise with the API response
 */
export const sendCustomerMessage = async (payload: any, files?: File[], authorizationToken?: string) => {
    const walApiUrl = window.WAL_API_URL || '';
    const url = `${walApiUrl}/api/v1/chat/send-customer-message`;

    const formData = new FormData();

    // Add all payload fields to FormData
    Object.keys(payload).forEach((key) => {
        const value = payload[key];

        if (value !== undefined && value !== null) {
            if (typeof value === 'object' && !Array.isArray(value)) {
                // Stringify nested objects
                formData.append(key, JSON.stringify(value));
            } else if (Array.isArray(value)) {
                // Handle arrays - stringify if not files
                formData.append(key, JSON.stringify(value));
            } else {
                formData.append(key, value.toString());
            }
        }
    });

    // Add files to FormData
    if (files && files.length > 0) {
        files.forEach((file) => {
            formData.append('attachments', file);
        });
    }

    // Add authorization token as separate field
    if (authorizationToken) {
        formData.append('authorizationToken', authorizationToken);
    }

    const headers: any = {};

    if (authorizationToken) {
        headers.Authorization = `Bearer ${authorizationToken}`;
    }

    // Don't set Content-Type header - let axios set it automatically for FormData
    // This ensures the boundary is set correctly for multipart/form-data

    const response = await axios.post(url, formData, {
        headers,
    });

    return response;
};
